import { axiosClient } from '../axiosClient';
import type { User, UserActivityItem, PropertyOption } from '@/types/user.types';

interface GetUsersParams {
  status?: string;
  role?: string;
  page?: number;
  limit?: number;
  search?: string;
  onlyProducers?: boolean;
  onlyFuncionarios?: boolean;
}

interface GetUsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

// Novo usuário: Proprietário manda `property` (cria propriedade nova); Funcionário
// manda `propriedadeId` (vincula a existente). Nunca os dois (o backend recusa).
export interface CreateUserData {
  name: string;
  phone: string;
  password: string;
  cargo: string;
  property?: {
    name: string;
    city: string;
    uf: string;
    area_total: number;
    reserva_legal: number;
    sistema_ilpf: number;
    pastagem_ha: number;
    area_agricola: number;
  };
  propriedadeId?: number;
}

export interface BlockUserData {
  reason: string;
  expires_at?: string;
  revoke_sessions?: boolean;
}

export interface ResetPasswordData {
  new_password: string;
  revoke_sessions?: boolean;
}

export interface UpdateUserData {
  name?: string;
  phone?: string;
  cargo?: string;
  status?: string;
  property?: {
    name?: string;
    city?: string;
    uf?: string;
    area_total?: number;
    reserva_legal?: number;
    sistema_ilpf?: number;
    pastagem_ha?: number;
    area_agricola?: number;
  };
}

export const getUsers = async (params?: GetUsersParams): Promise<GetUsersResponse> => {
  try {
    const response = await axiosClient.get('/admin/users', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch users:', error);
    throw error;
  }
};

export const getUserById = async (id: string): Promise<User> => {
  try {
    const response = await axiosClient.get(`/admin/users/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch user ${id}:`, error);
    throw error;
  }
};

export const getUserActivity = async (id: string): Promise<UserActivityItem[]> => {
  try {
    const response = await axiosClient.get(`/admin/users/${id}/activity`);
    return response.data?.activity ?? [];
  } catch (error) {
    console.error(`Failed to fetch activity for user ${id}:`, error);
    throw error;
  }
};

// Jornada completa: atividade recente + telas visitadas (navegação), já
// mescladas e ordenadas por data desc pelo backend.
export const getUserJourney = async (id: string): Promise<UserActivityItem[]> => {
  try {
    const response = await axiosClient.get(`/admin/users/${id}/journey`);
    return response.data?.journey ?? [];
  } catch (error) {
    console.error(`Failed to fetch journey for user ${id}:`, error);
    throw error;
  }
};

export const getProperties = async (search?: string): Promise<PropertyOption[]> => {
  try {
    const response = await axiosClient.get('/admin/properties', {
      params: search ? { search } : undefined,
    });
    return response.data?.properties ?? [];
  } catch (error) {
    console.error('Failed to fetch properties:', error);
    throw error;
  }
};

export const createUser = async (data: CreateUserData) => {
  try {
    const response = await axiosClient.post('/admin/users', data);
    return response.data;
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
};

export const updateUser = async (id: string, data: UpdateUserData) => {
  try {
    const response = await axiosClient.patch(`/admin/users/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update user ${id}:`, error);
    throw error;
  }
};

export const blockUser = async (id: string, data: BlockUserData) => {
  try {
    const response = await axiosClient.post(`/admin/users/${id}/block`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to block user ${id}:`, error);
    throw error;
  }
};

export const unblockUser = async (id: string) => {
  try {
    const response = await axiosClient.post(`/admin/users/${id}/unblock`);
    return response.data;
  } catch (error) {
    console.error(`Failed to unblock user ${id}:`, error);
    throw error;
  }
};

export const resetUserPassword = async (id: string, data: ResetPasswordData) => {
  try {
    const response = await axiosClient.post(`/admin/users/${id}/reset-password`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to reset password for user ${id}:`, error);
    throw error;
  }
};

export const deleteUser = async (id: string) => {
  try {
    const response = await axiosClient.delete(`/admin/users/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete user ${id}:`, error);
    throw error;
  }
};
